/* @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ImgSanity } from '@slite/components'

import { GrainCard } from '../../components/GrainCard'
import { Section } from '../../components/Section'
import { WebsiteText } from '../../components/WebsiteText'
import { MEDIUM_BREAKPOINT, SMALL_BREAKPOINT } from '../../utils/breakpoints'
import { SliteAgentIcon } from './SliteAgentIcon'

const titleCss = css`
  && {
    font-size: 46px;
    line-height: 1.3;
    max-width: 1040px;

    @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
      font-size: var(--font-size-2);
    }
  }
`

const agentNameCss = css`
  transform: translateY(6px);
`

const agentIconCss = css`
  margin-right: 4px;
`

const cardTitleCss = css`
  font-size: 20px;
`

const gridCss = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
  margin-top: 72px;
`

const cardCss = css`
  flex: 1;
  min-width: 360px;
  display: flex;
  flex-direction: column;
  padding: 24px 0px 40px;
`

const cardVisualCss = css`
  flex: 1;
  max-height: 305px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    height: auto;
    object-fit: contain;
    object-position: bottom;
    /* Mask (not overlay) fade so the card's grain shows through. */
    -webkit-mask-image: linear-gradient(to bottom, #000 80%, transparent 98%);
    mask-image: linear-gradient(to bottom, #000 80%, transparent 98%);
  }

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    min-height: 200px;
  }
`

export function SectionAgentBuiltIn() {
  const intl = useIntl()

  const cards = [
    {
      title: intl.formatMessage({
        id: 'agent.builtIn.company.title',
        defaultMessage: 'Knows your whole company',
      }),
      description: intl.formatMessage({
        id: 'agent.builtIn.company.desc',
        defaultMessage:
          'Acts as memory: knows the people, projects, decisions and relationships behind every request.',
      }),
      imageUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/bcd4a0583d5dbe843f1aa4c95029cbb510dc1727-756x610.png',
    },
    {
      title: intl.formatMessage({
        id: 'agent.builtIn.trust.title',
        defaultMessage: 'Knows what to trust',
      }),
      description: intl.formatMessage({
        id: 'agent.builtIn.trust.desc',
        defaultMessage:
          'Considers freshness, authority and verification before using information in an answer.',
      }),
      imageUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/3b0ee3d0157d025f35495d4df21c4a7cc40f831f-774x610.png',
    },
    {
      title: intl.formatMessage({
        id: 'agent.builtIn.evidence.title',
        defaultMessage: 'Shows the evidence',
      }),
      description: intl.formatMessage({
        id: 'agent.builtIn.evidence.desc',
        defaultMessage:
          'Returns cited answer with its sources, owners and timestamps, so teams can trust the output, not just read it.',
      }),
      imageUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/d454d31fc2be7983cb3aef69d3d192ec76860d64-774x610.png',
    },
  ]

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={120}>
      <WebsiteText variant="title" htmlTag="h2" css={titleCss}>
        <FormattedMessage
          id="agent.builtIn.title"
          defaultMessage="Your knowledge base, with an Agent built in."
        />
      </WebsiteText>
      <WebsiteText
        variant="title"
        htmlTag="p"
        className="margin-top-40"
        css={titleCss}
      >
        <FormattedMessage
          id="agent.builtIn.subtitle"
          defaultMessage="<agent>Slite Agent</agent> doesn’t just search company knowledge. It understands it, improves it and keeps every change under human control."
          values={{
            agent: (chunks) => (
              <span
                className="flexbox-inline flexbox-align-center flexbox-gap-8 saturn-orange padding-right-4"
                css={agentNameCss}
              >
                <SliteAgentIcon size={48} css={agentIconCss} />
                <span>{chunks}</span>
              </span>
            ),
          }}
        />
      </WebsiteText>

      <div css={gridCss}>
        {cards.map((card) => (
          <GrainCard key={card.title} css={cardCss}>
            <div css={cardVisualCss}>
              <ImgSanity src={card.imageUrl} alt={card.title} />
            </div>
            <WebsiteText
              variant="smallerTitle"
              htmlTag="h3"
              className="shade-00 padding-horizontal-24 padding-top-24"
              css={cardTitleCss}
            >
              {card.title}
            </WebsiteText>
            <p className="font-size-6 line-height-large shade-10 margin-top-8 padding-horizontal-24">
              {card.description}
            </p>
          </GrainCard>
        ))}
      </div>
    </Section>
  )
}
